import { AuditIcon } from "@minket/brand/icons/audit";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { cn } from "@minket/ui/lib/utils";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { actionGroups, actionLabel, actionLabels } from "@/components/audit/actions";
import {
  AuditInspector,
  Changes,
  type Entry,
  type Names,
  objectOf,
  Signature,
} from "@/components/audit/entry";
import { selectStyle } from "@/components/ops/select";
import { When } from "@/components/ops/when";
import { GhostTable } from "@/components/page/ghost";
import {
  InspectorLayout,
  useInspectorFits,
  useLedger,
  useSelection,
} from "@/components/page/inspector";
import { Page } from "@/components/page/page";
import { OrgScope } from "@/components/page/scope";
import { QueryView } from "@/components/page/states";
import { endpoints } from "@/lib/endpoints";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

/**
 * The log's filters live in the URL, so a toast can link straight to an act's entry. They are
 * read where they are used rather than by a route validator, which would sit in the route tree
 * every page loads.
 */
interface AuditSearch {
  action?: string;
  project_id?: string;
  selected?: string;
}

const text = (value: unknown) => (typeof value === "string" && value !== "" ? value : undefined);

export const Route = createFileRoute("/audit")({
  component: Audit,
});

const columns = ["Signed by", "Act", "Object", "Time, UTC"];
const widths = ["14rem", undefined, "14rem", "10rem"];
const ghost = () => <GhostTable columns={columns} widths={widths} label="audit entries" />;

function Audit() {
  return (
    <Page title="Audit log">
      <OrgScope
        will="Every change made in it appears here, signed."
        icon={<AuditIcon />}
        preview={ghost}
      >
        {(org) => <AuditLog orgId={org.organization_id} />}
      </OrgScope>
    </Page>
  );
}

type Cursor = { after: string } | { before: string } | null;

function AuditLog({ orgId }: { orgId: string }) {
  const { projects } = useWorkspace();
  const raw = useSearch({ strict: false }) as Record<string, unknown>;
  const search: AuditSearch = { action: text(raw.action), project_id: text(raw.project_id) };
  const navigate = useNavigate();
  const [cursor, setCursor] = useState<Cursor>(null);
  const members = useQuery(apiQuery(endpoints.members, { orgId }));
  const query = useQuery({
    ...apiQuery(
      endpoints.audit,
      { orgId },
      { limit: 50, action: search.action, project_id: search.project_id, ...(cursor ?? {}) },
    ),
    placeholderData: keepPreviousData,
  });
  const filter = (next: { action?: string; project_id?: string }) => {
    setCursor(null);
    void navigate({
      to: "/audit",
      search: { action: search.action, project_id: search.project_id, ...next },
      replace: true,
    });
  };
  const filtered = search.action !== undefined || search.project_id !== undefined;
  const names: Names = new Map(
    (members.data?.data ?? []).map((member) => [
      member.user.id,
      { name: member.user.name, email: member.user.email },
    ]),
  );
  const projectName = (id: string | null) =>
    id ? (projects?.data?.data.find((project) => project.id === id)?.name ?? null) : null;
  return (
    <div className="grid min-w-0 content-start gap-5">
      <search>
        <form
          aria-label="Filter the audit log"
          className="flex flex-wrap items-end gap-2.5"
          onSubmit={(event) => event.preventDefault()}
        >
          <label className="grid min-w-0 gap-1">
            <span className="type-label">Act</span>
            <select
              className={cn(selectStyle, "w-auto max-w-full")}
              value={search.action ?? ""}
              onChange={(event) => filter({ action: event.currentTarget.value || undefined })}
            >
              <option value="">Every act</option>
              {actionGroups.map((group) => (
                <optgroup key={group.area} label={group.area}>
                  {group.actions.map((value) => (
                    <option key={value} value={value}>
                      {actionLabels[value]}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="grid min-w-0 gap-1">
            <span className="type-label">Project</span>
            <select
              className={cn(selectStyle, "w-auto max-w-full")}
              value={search.project_id ?? ""}
              onChange={(event) => filter({ project_id: event.currentTarget.value || undefined })}
            >
              <option value="">Every project</option>
              {(projects?.data?.data ?? []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          {filtered ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => filter({ action: undefined, project_id: undefined })}
            >
              Clear filters
            </Button>
          ) : null}
        </form>
      </search>
      <QueryView query={query} noun="audit entries" preview={ghost}>
        {(page) =>
          page.data.length === 0 ? (
            <EmptyState
              data-state="empty"
              icon={<AuditIcon />}
              title={filtered ? "No entries match" : "Nothing recorded yet"}
              description={
                filtered
                  ? "Nothing signed matches these filters. Clear them to see the whole log."
                  : "The first change made in the console or with a key is signed here."
              }
              preview={ghost()}
            />
          ) : (
            <Ledger
              entries={page.data}
              names={names}
              projectName={projectName}
              hasMore={page.has_more}
              cursor={cursor}
              onPage={setCursor}
            />
          )
        }
      </QueryView>
    </div>
  );
}

function Ledger({
  entries,
  names,
  projectName,
  hasMore,
  cursor,
  onPage,
}: {
  entries: readonly Entry[];
  names: Names;
  projectName: (id: string | null) => string | null;
  hasMore: boolean;
  cursor: Cursor;
  onPage: (cursor: Cursor) => void;
}) {
  const ledger = useLedger(entries.map((entry) => entry.id));
  // Beside the inspector the ledger keeps who, what and when; the object is in the inspector.
  const compact = ledger.shown !== null;
  const shown = compact ? columns.filter((column) => column !== "Object") : columns;
  const { selected } = useSelection();
  const fits = useInspectorFits();
  const first = entries[0];
  const last = entries.at(-1);
  // Paging back (`before`), `has_more` speaks of newer entries; forward, of older ones.
  const back = cursor !== null && "before" in cursor;
  const newer = back ? hasMore : cursor !== null;
  const older = back ? true : hasMore;
  return (
    <InspectorLayout
      ledger={ledger}
      inspector={(id) => {
        const entry = entries.find((item) => item.id === id);
        return entry ? (
          <AuditInspector
            entry={entry}
            names={names}
            project={projectName(entry.project_id)}
            onClose={ledger.close}
          />
        ) : null;
      }}
    >
      <Table aria-label="Audit entries">
        <colgroup>
          {shown.map((column) => (
            <col key={column} style={{ width: widths[columns.indexOf(column)] }} />
          ))}
        </colgroup>
        <TableHeader>
          <TableRow>
            {shown.map((column) => (
              <TableHead key={column} className={cn(column === "Time, UTC" && "text-right")}>
                {column}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => {
            const object = objectOf(entry);
            const project = projectName(entry.project_id);
            const open = !fits && selected === entry.id;
            return [
              <TableRow key={entry.id} {...ledger.row(entry.id)} className="cursor-pointer">
                <TableCell className="py-3">
                  <Signature entry={entry} names={names} />
                </TableCell>
                <TableCell className="min-w-44 py-3">
                  <Link
                    to="/audit"
                    search={({ selected: current, ...previous }: AuditSearch) =>
                      // Below 1180 px the link opens the entry in place, and closes it again.
                      open && current === entry.id ? previous : { ...previous, selected: entry.id }
                    }
                    aria-expanded={fits ? undefined : open}
                    {...ledger.link(entry.id)}
                    className="rounded-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  >
                    {actionLabel(entry.action)}
                  </Link>
                </TableCell>
                {compact ? null : (
                  <TableCell className="min-w-40 py-3">
                    <span className="block">
                      {object.kind}
                      {object.name ? (
                        <span className="text-muted-foreground"> {object.name}</span>
                      ) : null}
                    </span>
                    {project ? (
                      <span className="block text-[12.5px] text-muted-foreground">
                        in {project}
                      </span>
                    ) : null}
                  </TableCell>
                )}
                <TableCell className="py-3 text-right">
                  <When value={entry.created_at} />
                </TableCell>
              </TableRow>,
              open ? (
                <TableRow key={`${entry.id}-open`} className="hover:bg-transparent">
                  <TableCell colSpan={shown.length} className="bg-accent/40 pb-4">
                    <Changes entry={entry} />
                  </TableCell>
                </TableRow>
              ) : null,
            ];
          })}
        </TableBody>
      </Table>
      {newer || older ? (
        <nav aria-label="Audit pages" className="flex flex-wrap gap-2 pt-3">
          {newer && first ? (
            <Button variant="outline" size="sm" onClick={() => onPage({ before: first.id })}>
              Newer entries
            </Button>
          ) : null}
          {older && last ? (
            <Button variant="outline" size="sm" onClick={() => onPage({ after: last.id })}>
              Older entries
            </Button>
          ) : null}
        </nav>
      ) : null}
    </InspectorLayout>
  );
}
